// Copyright (c) 2026. Hardfin, Inc. All rights reserved.
// Hardfin Confidential - Restricted
import {
  ShipmentQuarantineEntry,
  ShipmentQuarantineProperty,
} from '@/audit/api/types';
import { Link } from '@/components/link';
import { Stack, Typography } from '@mui/material';
import { ReactNode } from 'react';

interface Props {
  property: ShipmentQuarantineProperty;
}

/** One held serial's line in the list. */
function QuarantineLine(props: { children: ReactNode }) {
  return (
    <Typography variant="caption" component="li" sx={{ display: 'list-item' }}>
      {props.children}
    </Typography>
  );
}

/**
 * Formats an item as "sku (name)", falling back to the name for items with no
 * SKU.
 */
function itemHeading(name: string, sku: string | null | undefined): string {
  return sku ? `${sku} (${name})` : name;
}

/**
 * Describes why one serial is held. A serial Hardfin already has names the
 * conflicting items; a serial Hardfin has no record of is simply unknown.
 */
function QuarantineEntry(props: { entry: ShipmentQuarantineEntry }) {
  const { entry } = props;

  if (entry.quarantineType === 'SERIAL_PATTERN_MISMATCH') {
    return (
      <QuarantineLine>
        Serial {entry.serial} reported by 3PL does not match your organization's
        configured serial patterns
      </QuarantineLine>
    );
  }

  if (entry.quarantineType === 'MISSING_SERIAL') {
    return (
      <QuarantineLine>
        Unit reported by 3PL as{' '}
        {itemHeading(entry.reportedItem.name, entry.reportedItem.sku)} with no
        serial
      </QuarantineLine>
    );
  }

  if (entry.quarantineType === 'DUPLICATE_IN_ORDER') {
    return (
      <QuarantineLine>
        Serial {entry.serial} reported by 3PL more than once on this shipment
      </QuarantineLine>
    );
  }

  if (entry.quarantineType === 'DUPLICATE_ALREADY_EXISTS') {
    // Name the item only when the two disagree.
    const isDifferentItem =
      entry.existingItem !== null &&
      entry.existingItem.id !== entry.reportedItem.id;

    return (
      <QuarantineLine>
        {entry.existingAsset ? (
          <Link
            to="/assets/$assetKey"
            params={{ assetKey: entry.existingAsset.key }}
          >
            {entry.serial}
          </Link>
        ) : (
          entry.serial
        )}{' '}
        reported by 3PL as{' '}
        {itemHeading(entry.reportedItem.name, entry.reportedItem.sku)} but
        already exists in Hardfin
        {isDifferentItem && entry.existingItem
          ? ` as ${itemHeading(entry.existingItem.name, entry.existingItem.sku)}`
          : ''}
      </QuarantineLine>
    );
  }

  // Entries audited before quarantineType existed carry none, and a hold with
  // no existing item was an unknown serial in every one of them.
  if (entry.existingItem === null) {
    return (
      <QuarantineLine>
        Unknown serial {entry.serial} reported by 3PL
      </QuarantineLine>
    );
  }

  return (
    <QuarantineLine>
      {entry.existingAsset ? (
        <Link
          to="/assets/$assetKey"
          params={{ assetKey: entry.existingAsset.key }}
        >
          {entry.serial}
        </Link>
      ) : (
        entry.serial
      )}{' '}
      reported by 3PL as{' '}
      {itemHeading(entry.reportedItem.name, entry.reportedItem.sku)} but already
      exists in Hardfin as{' '}
      {itemHeading(entry.existingItem.name, entry.existingItem.sku)}
    </QuarantineLine>
  );
}

/**
 * Renders the serials a shipment held, each with the item the 3PL reported it
 * under and the item Hardfin has it under. The hold is an addition rather than a
 * change, so there is no old value to diff against.
 */
export function AuditShipmentQuarantineLabel(props: Props) {
  const entries = props.property.newValue ?? [];

  if (entries.length === 0) {
    return null;
  }

  return (
    <Stack>
      <Typography component="span" variant="caption" fontWeight="bold">
        {props.property.name}:
      </Typography>
      <Stack
        component="ul"
        sx={{ listStyleType: 'disc', margin: 0, paddingLeft: 4 }}
      >
        {entries.map((entry, index) => (
          <QuarantineEntry
            key={`${entry.serial}-${index.toString()}`}
            entry={entry}
          />
        ))}
      </Stack>
    </Stack>
  );
}
